export interface AiAssistantUiContext {
  siteId?: string;
  siteName?: string;
  chargerId?: string;
  chargerName?: string;
  evseId?: string;
  evseName?: string;
  vehicleId?: string;
  vehicleName?: string;
  promptPresetName?: string;
}

export interface ParsedAiAssistantUserMessage {
  context?: AiAssistantUiContext;
  message: string;
}

const CONTEXT_TAG_NAME = 'context-from-ui';
const CONTEXT_PATTERN = /^<context-from-ui>\s*([\S\s]*?)\s*<\/context-from-ui>\s*/;

export type AiAssistantUiContextKey = 'site' | 'charger' | 'evse' | 'vehicle';

export interface AiAssistantUiContextItem {
  key: AiAssistantUiContextKey;
  label: string;
  value: string;
}

export const buildAiAssistantUiContextItems = (context?: AiAssistantUiContext): AiAssistantUiContextItem[] => {
  if (!context) {
    return [];
  }

  return [
    ...(context.siteName ? [{ key: 'site' as const, label: 'Site', value: context.siteName }] : []),
    ...(context.chargerName ? [{ key: 'charger' as const, label: 'Charger', value: context.chargerName }] : []),
    ...(context.evseName ? [{ key: 'evse' as const, label: 'EVSE', value: context.evseName }] : []),
    ...(context.vehicleName ? [{ key: 'vehicle' as const, label: 'Vehicle', value: context.vehicleName }] : []),
  ];
};

export const stripAiAssistantUiContextParts = (
  context: AiAssistantUiContext | undefined,
  removedKeys: ReadonlySet<AiAssistantUiContextKey>
): AiAssistantUiContext | undefined => {
  if (!context || removedKeys.size === 0) {
    return context;
  }

  return {
    ...context,
    ...(removedKeys.has('site') ? { siteId: undefined, siteName: undefined } : {}),
    ...(removedKeys.has('charger') ? { chargerId: undefined, chargerName: undefined } : {}),
    ...(removedKeys.has('evse') ? { evseId: undefined, evseName: undefined } : {}),
    ...(removedKeys.has('vehicle') ? { vehicleId: undefined, vehicleName: undefined } : {}),
  };
};

export const hasAiAssistantUiContext = (context?: AiAssistantUiContext): boolean => {
  return !!(
    context?.siteId ||
    context?.siteName ||
    context?.chargerId ||
    context?.chargerName ||
    context?.evseId ||
    context?.evseName ||
    context?.vehicleId ||
    context?.vehicleName ||
    context?.promptPresetName
  );
};

export const prependAiAssistantUiContext = (message: string, context?: AiAssistantUiContext): string => {
  if (!hasAiAssistantUiContext(context)) {
    return message;
  }

  return `<${CONTEXT_TAG_NAME}>\n${JSON.stringify(context)}\n</${CONTEXT_TAG_NAME}>\n\n${message}`;
};

export const parseAiAssistantUserMessage = (message: string): ParsedAiAssistantUserMessage => {
  const match = message.match(CONTEXT_PATTERN);
  if (!match) {
    return { message };
  }

  const [, rawContext] = match;
  try {
    const parsedContext = JSON.parse(rawContext) as AiAssistantUiContext;
    return {
      context: parsedContext,
      message: message.slice(match[0].length).trimStart(),
    };
  } catch {
    return { message };
  }
};

export const getAiAssistantUserMessageDisplayText = (message: string): string => {
  const parsedMessage = parseAiAssistantUserMessage(message);
  return parsedMessage.context?.promptPresetName || parsedMessage.message;
};

export const stripAiAssistantUiContext = (message: string): string => parseAiAssistantUserMessage(message).message;
